import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_CONFIG } from '../config';
import { notifySessionExpired } from './sessionExpiry';

// Bump trigger: 21

const developerApi = axios.create({
  baseURL: API_CONFIG.developerApiBase,
});

developerApi.interceptors.request.use(async (config) => {
  const { tokens } = await fetchAuthSession();
  const token = tokens?.idToken?.toString();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Response error handler: an expired Cognito session makes the developer API
 * return 401. Redirect to a clean re-login instead of surfacing raw 401 text.
 * Exported so the 401→redirect wiring is directly unit-testable. Always
 * rethrows so callers still see the rejection (e.g. ApiErrorAlert).
 */
export function handleDeveloperApiError(error) {
  if (error?.response?.status === 401) notifySessionExpired();
  return Promise.reject(error);
}

developerApi.interceptors.response.use((response) => response, handleDeveloperApiError);

// Keys
export const listKeys = (tenantId) =>
  developerApi.get('/developer/keys', { params: { tenantId } }).then((r) => r.data);

export const createKey = (body) =>
  developerApi.post('/developer/keys', body).then((r) => r.data);

export const revokeKey = (keyId) =>
  developerApi.delete(`/developer/keys/${keyId}`).then((r) => r.data);

// Domains
export const listDomains = () =>
  developerApi.get('/developer/domains').then((r) => r.data);

export const registerDomain = (body) =>
  developerApi.post('/developer/domains', body).then((r) => r.data);

export const verifyDomain = (id) =>
  developerApi.post(`/developer/domains/${id}/verify`).then((r) => r.data);

export const deleteDomain = (id) =>
  developerApi.delete(`/developer/domains/${id}`).then((r) => r.data);

// Webhooks
export const listWebhooks = (tenantId) =>
  developerApi.get('/developer/webhooks', { params: { tenantId } }).then((r) => r.data);

export const createWebhook = (body) =>
  developerApi.post('/developer/webhooks', body).then((r) => r.data);

export const updateWebhook = (id, body) =>
  developerApi.put(`/developer/webhooks/${id}`, body).then((r) => r.data);

export const deleteWebhook = (id) =>
  developerApi.delete(`/developer/webhooks/${id}`).then((r) => r.data);

// API Logs
export const getApiLogs = (params) =>
  developerApi.get('/developer/logs/api', { params }).then((r) => r.data);

// Webhook deliveries
export const getWebhookDeliveries = (webhookId, params) =>
  developerApi.get(`/developer/webhooks/${webhookId}/deliveries`, { params }).then((r) => r.data);

export const retryWebhookDelivery = (webhookId, deliveryId) =>
  developerApi.post(`/developer/webhooks/${webhookId}/deliveries/${deliveryId}/retry`).then((r) => r.data);

// Playground token — short-lived st_* for Scalar API reference
// Never expose sk_live_* or sk_test_* in the browser; this is the only credential
// that reaches the frontend.
export const getPlaygroundToken = () =>
  developerApi.get('/developer/playground-token').then((r) => r.data);

// Legal
export const getLegalStatus = () =>
  developerApi.get('/developer/legal/status').then((r) => r.data);

export const acceptTos = () =>
  developerApi.post('/developer/legal/accept-tos').then((r) => r.data);

export const signBaa = () =>
  developerApi.post('/developer/legal/sign-baa').then((r) => r.data);

export const getBaaRecord = () =>
  developerApi.get('/developer/legal/baa-record').then((r) => r.data);

// Billing
export const getBilling = () =>
  developerApi.get('/developer/billing').then((r) => r.data);

export const createCheckoutSession = (body) =>
  developerApi.post('/developer/billing/checkout', body).then((r) => r.data);

export const getCheckoutStatus = (sessionId) =>
  developerApi.get('/developer/billing/checkout-status', { params: { sessionId } }).then((r) => r.data);

export const updateOverage = (body) =>
  developerApi.post('/developer/billing/overage', body).then((r) => r.data);

// Inference balance — separate pre-paid ledger from monthly plan credits.
// See backend DeveloperBillingHandler /inference, /inference/fund, /inference/auto-recharge.
export const getInferenceBalance = () =>
  developerApi.get('/developer/billing/inference').then((r) => r.data);

export const fundInferenceBalance = (amountCents) =>
  developerApi.post('/developer/billing/inference/fund', { amountCents }).then((r) => r.data);

export const updateInferenceAutoRecharge = (body) =>
  developerApi.put('/developer/billing/inference/auto-recharge', body).then((r) => r.data);

export default developerApi;
